import { formatDateTime12Hour } from "@circuitry-ai/doc-ui";
import { TranscriptMessage } from "./CallLogDrawer";
import { Icon } from "@iconify/react";


const hasSecondsFromStart = (message: TranscriptMessage): boolean =>
    message.secondsFromStart !== undefined && message.secondsFromStart !== null;

interface ToolTranscriptCardProps {
    message: TranscriptMessage;
    index: number;
    expanded: boolean;
    onToggle: (index: number) => void;
}

const ToolTranscriptExpanded: React.FC<{
    message: TranscriptMessage;
    jsonText: string;
}> = ({ message, jsonText }) => {
    const messageText = message.message ?? message.text;

    return (
        <div className="mt-3 w-full min-w-0 overflow-hidden rounded-lg border border-divider bg-content1">
            {jsonText ? (
                <pre className="max-h-[min(70vh,28rem)] w-full min-w-0 overflow-auto p-3 font-mono text-xs leading-relaxed text-default-800 [overflow-wrap:anywhere] whitespace-pre">
                    {jsonText}
                </pre>
            ) : (
                <p className="p-3 text-sm text-default-500">No tool details available.</p>
            )}
            {messageText ? (
                <div className="border-t border-divider p-3">
                    <p className="mb-1 text-xs font-medium text-default-500">Message</p>
                    <p className="break-words text-sm text-default-700">{messageText}</p>
                </div>
            ) : null}
        </div>
    );
};

const ToolTranscriptMeta: React.FC<{ message: TranscriptMessage; }> = ({
    message,
}) => {
    if (!message.timestamp && !hasSecondsFromStart(message)) return null;

    return (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0 text-xs text-default-500">
            {message.timestamp ? <span>{formatDateTime12Hour(message.timestamp)}</span> : null}
            {hasSecondsFromStart(message) ? (
                <span className="text-default-400">
                    Offset: {String(message.secondsFromStart)}s
                </span>
            ) : null}
        </div>
    );
};

export const ToolTranscriptCard: React.FC<ToolTranscriptCardProps> = ({
    message,
    index,
    expanded,
    onToggle,
}) => {
    const getToolName = (details: Record<string, unknown> | undefined): string | null =>
        details && typeof details.name === 'string' ? String(details.name) : null;

    const formatToolDetailsJson = (details: Record<string, unknown> | undefined): string => {
        if (!details || Object.keys(details).length === 0) return '';
        try {
            return JSON.stringify(details, null, 2);
        } catch {
            return String(details);
        }
    };
    const isCall = message.type === 'tool_call';
    const toolName = getToolName(message.tool_details);
    const jsonText = formatToolDetailsJson(message.tool_details);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        onToggle(index);
    };

    return (
        <div className="w-full min-w-0 shrink-0">
            <div
                className={`w-full min-w-0 rounded-xl border p-4 cursor-pointer text-left transition-colors ${isCall ? 'bg-secondary-50 border-secondary-200' : 'bg-success-50 border-success-200'
                    }`}
                onClick={() => onToggle(index)}
                onKeyDown={handleKeyDown}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                aria-label={expanded ? 'Hide details' : 'View details'}
            >
                <div className="flex flex-wrap items-start justify-between gap-2 gap-y-1">
                    <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-semibold">
                                {isCall ? 'Tool Call' : 'Tool Result'}
                                {toolName ? (
                                    <span className="font-mono text-default-700 font-normal"> — {toolName}</span>
                                ) : null}
                            </span>
                        </div>
                        <ToolTranscriptMeta message={message} />
                    </div>
                    <Icon icon={expanded ? 'mdi:chevron-up' : 'mdi:chevron-down'} className="w-5 h-5 shrink-0 text-default-500" />
                </div>
                {expanded ? <ToolTranscriptExpanded message={message} jsonText={jsonText} /> : null}
            </div>
        </div>
    );
};